<script setup lang="ts">
import type { Comment, CommentSubmittedPayload, Content, PageResult } from '~/types/blog'
import QRCode from 'qrcode'
import { normalizeFieldBool, normalizeFieldNumber, normalizeFieldText } from '~/composables/useCustomFields'
import { buildLocalSubmittedComment, insertLocalComment } from '~/composables/commentLocalInsert'
import { scrollToElementWithOffset } from '~/composables/useAnchorScroll'
import { parsePostRouteParams, resolvePostPath } from '~/composables/usePermalink'
import { useCommentAnchorScroll } from '~/composables/useCommentAnchorScroll'
import { useEmbeddedPostCards } from '~/composables/useEmbeddedPostCards'

const route = useRoute()
const blogApi = useBlogApi()
const siteData = await useSiteData()
const { isStaleNavigation } = useNavigationStaleGuard()
const siteBaseUrl = resolveSiteBaseUrl(siteData?.setting?.customFields)
const pathParsed = resolvePostPath(siteData?.setting?.customFields, route.path)
const routeParsed = parsePostRouteParams(route)
const slug = String(routeParsed.slug || pathParsed?.slug || '')
const cid = routeParsed.cid || Number(pathParsed?.cid || 0)
const qrOpen = ref(false)

const createStaleContentFallback = (): Content => ({
  ID: 0,
  title: '',
  slug: '',
  type: 'post',
  status: 'draft'
})

const { data: content } = await useAsyncData(`post:${route.path}`, async (_nuxtApp, { signal }) => {
  if (slug) {
    try {
      return await blogApi.getContentBySlug(slug, { signal })
    } catch (error) {
      if (isStaleNavigation() || isAbortLikeError(error)) {
        return createStaleContentFallback()
      }
      if (!cid) {
        throw error
      }
    }
  }
  if (cid > 0) {
    try {
      return await blogApi.getContent(cid, { signal })
    } catch (error) {
      if (isStaleNavigation() || isAbortLikeError(error)) {
        return createStaleContentFallback()
      }
      throw error
    }
  }
  if (isStaleNavigation()) {
    return createStaleContentFallback()
  }
  if (import.meta.client) {
    return createStaleContentFallback()
  }
  throw createError({ status: 404, message: '文章不存在' })
})

if ((!content.value?.ID || content.value.type !== 'post') && !isStaleNavigation()) {
  if (import.meta.server) {
    throw createError({ status: 404, message: '文章不存在' })
  }
  showError({ statusCode: 404, message: '文章不存在' })
}

const settingsFields = siteData?.setting?.customFields
const commentListMode = computed(() => {
  const mode = normalizeFieldText(settingsFields, 'commentListMode', 'pager').toLowerCase()
  return mode === 'pager' || mode === 'pagination' ? 'pager' : 'infinite'
})
const isCommentPagerMode = computed(() => commentListMode.value === 'pager')
const commentPageSize = computed(() => {
  const size = Math.floor(normalizeFieldNumber(settingsFields, 'commentPageSize', 10))
  return Math.min(100, Math.max(1, size || 10))
})
const currentCommentPage = computed(() => {
  if (!isCommentPagerMode.value) {
    return 1
  }
  const raw = Number(route.query.cpage || 1)
  if (!Number.isFinite(raw) || raw < 1) {
    return 1
  }
  return Math.floor(raw)
})
const requestCommentPage = computed(() => (isCommentPagerMode.value ? currentCommentPage.value : 1))
const commentDataKey = computed(() =>
  `comments:${content.value?.ID}:${commentListMode.value}:${requestCommentPage.value}:${commentPageSize.value}`
)
const { data: firstComments, pending: firstCommentsPending } = await useAsyncData<PageResult<Comment>>(
  commentDataKey,
  () => {
    const contentID = Number(content.value?.ID || 0)
    if (contentID <= 0) {
      return Promise.resolve({
        list: [],
        total: 0,
        page: requestCommentPage.value,
        pageSize: commentPageSize.value
      })
    }
    return blogApi.getCommentTree(contentID, { page: requestCommentPage.value, pageSize: commentPageSize.value })
  }
)
const comments = ref<Comment[]>([])
const commentPage = ref(1)
const commentTotal = ref(0)
const commentLoading = ref(false)
const commentNoMore = ref(false)
let commentScrollFrame = 0
const hasMoreComments = computed(() => !isCommentPagerMode.value && !commentNoMore.value)
const showCommentPagerSkeleton = computed(() => isCommentPagerMode.value && firstCommentsPending.value)
const showCommentInfiniteSkeleton = computed(() => !isCommentPagerMode.value && firstCommentsPending.value && !comments.value.length)
const allowComment = computed(() => content.value?.allowComment !== false)
const hasComments = computed(() => comments.value.length > 0)

const replyingTo = ref<Comment | null>(null)
const onReply = (item: Comment) => {
  replyingTo.value = item
  if (import.meta.client) {
    nextTick(() => {
      const replyForm = document.getElementById(`reply-form-${item.ID}`)
      const target = replyForm || document.getElementById(`comment-${item.ID}`)
      scrollToElementWithOffset(target)
    })
  }
}

const clearReply = () => {
  replyingTo.value = null
}

let tempCommentID = 0

const nextTempCommentID = () => {
  tempCommentID -= 1
  return tempCommentID
}

const resolveReplyTarget = (parentID: number) => {
  for (let rootIndex = 0; rootIndex < comments.value.length; rootIndex += 1) {
    const root = comments.value[rootIndex]
    if (root.ID === parentID) {
      return { rootIndex, parent: root }
    }
    const child = (root.children || []).find(item => item.ID === parentID)
    if (child) {
      return { rootIndex, parent: child }
    }
  }
  return null
}

const onCommentSubmitted = (payload: CommentSubmittedPayload, replyItem: Comment | null = null) => {
  const parentID = Number(payload.parentId || 0)
  const target = parentID > 0 ? resolveReplyTarget(parentID) : null
  const node = buildLocalSubmittedComment(payload, replyItem || target?.parent || null, nextTempCommentID)
  comments.value = insertLocalComment(comments.value, node, parentID)

  commentTotal.value += 1
  if (content.value) {
    content.value.commentCount = Number(content.value.commentCount || 0) + 1
  }
  clearReply()
}
const showToc = computed(() => normalizeFieldBool(content.value?.customFields, 'showToc', true))
const { tocItems, tocBoxRef, tocCollapsed, hasToc, onTocChange, toggleToc } = useContentToc({ enabled: showToc })
const { postCards } = useEmbeddedPostCards({
  source: () => content.value?.markdown || content.value?.content || '',
  fields: settingsFields,
  siteBaseUrl
})

const siteName = normalizeFieldText(siteData?.setting?.customFields, 'siteName')
const siteDescription = normalizeFieldText(
  siteData?.setting?.customFields,
  'siteDescription',
  normalizeFieldText(siteData?.setting?.customFields, 'seoDescription', '')
)
const pageDescription = computed(() => content.value?.summary || siteDescription)
const gravatarMirror = normalizeFieldText(siteData?.setting?.customFields, 'gravatarMirror', 'https://www.gravatar.com/avatar')
const pageKeywords = computed(() =>
  [
    ...((content.value?.tags || []).map(item => item.name)),
    normalizeFieldText(siteData?.setting?.customFields, 'seoKeywords', '')
  ]
    .filter(Boolean)
    .join(',')
)
const seoTitle = computed(() => `${content.value?.title || '文章'} - ${siteName}`)
const articleUrl = computed(() => {
  const base = String(siteBaseUrl || '').replace(/\/+$/, '')
  return `${base}${route.path}`
})
const qrImageUrl = ref('')
const copyState = ref<'idle' | 'success' | 'error'>('idle')
const liked = ref(false)
const likeEffectToken = ref(0)
const contentLike = useOptimisticLike('content', blogApi.likeContent)
let copyStateTimer: ReturnType<typeof setTimeout> | null = null

const syncLikedState = () => {
  liked.value = contentLike.isLiked(content.value)
}

const updateQrImage = async (url: string) => {
  try {
    qrImageUrl.value = await QRCode.toDataURL(url, {
      width: 260,
      margin: 1,
      color: {
        dark: '#111827',
        light: '#FFFFFFFF'
      }
    })
  } catch {
    qrImageUrl.value = ''
  }
}

const setCopyState = (state: 'idle' | 'success' | 'error') => {
  copyState.value = state
  if (copyStateTimer) {
    clearTimeout(copyStateTimer)
    copyStateTimer = null
  }
  if (state !== 'idle') {
    copyStateTimer = setTimeout(() => {
      copyState.value = 'idle'
    }, 1800)
  }
}

const copyShareLink = async () => {
  const text = articleUrl.value
  if (!import.meta.client || !text) {
    return
  }
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      setCopyState('success')
      return
    }
    const input = document.createElement('textarea')
    input.value = text
    input.setAttribute('readonly', 'readonly')
    input.style.position = 'fixed'
    input.style.opacity = '0'
    document.body.appendChild(input)
    input.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(input)
    setCopyState(ok ? 'success' : 'error')
  } catch {
    setCopyState('error')
  }
}

const likeCurrentContent = () => {
  contentLike.like(content.value, () => {
    liked.value = true
    likeEffectToken.value += 1
  })
}

const resetCommentPage = (payload?: PageResult<Comment> | null) => {
  const list = payload?.list || []
  commentTotal.value = Number(payload?.total || 0)
  comments.value = [...list]
  commentPage.value = isCommentPagerMode.value ? currentCommentPage.value + 1 : 2
  commentNoMore.value = isCommentPagerMode.value
    ? true
    : comments.value.length >= commentTotal.value || list.length < commentPageSize.value
}

const loadMoreComments = async () => {
  if (isCommentPagerMode.value || commentLoading.value || commentNoMore.value) {
    return
  }
  const contentID = Number(content.value?.ID || 0)
  if (contentID <= 0) {
    commentNoMore.value = true
    return
  }
  commentLoading.value = true
  try {
    const batch = await blogApi.getCommentTree(contentID, { page: commentPage.value, pageSize: commentPageSize.value })
    const next = batch.list || []
    commentTotal.value = Number(batch.total || 0)
    comments.value = [...comments.value, ...next]
    if (comments.value.length >= commentTotal.value || next.length < commentPageSize.value) {
      commentNoMore.value = true
      return
    }
    commentPage.value += 1
  } catch {
    // 网络异常时停止自动滚动加载，避免反复重试。
    commentNoMore.value = true
  } finally {
    commentLoading.value = false
  }
}

const { onDocumentAnchorClick, settleRouteHashVisible } = useCommentAnchorScroll({
  comments,
  contentId: () => content.value?.ID || 0,
  commentPageSize,
  isCommentPagerMode,
  currentCommentPage,
  commentNoMore,
  loadMoreComments
})

watch(
  () => firstComments.value,
  value => {
    resetCommentPage(value || null)
    if (import.meta.client && route.hash) {
      nextTick(() => {
        void settleRouteHashVisible(route.hash, 'auto')
      })
    }
  },
  { immediate: true }
)

const onCommentScroll = () => {
  if (!import.meta.client || isCommentPagerMode.value || commentLoading.value || commentNoMore.value || !comments.value.length) {
    return
  }
  if (commentScrollFrame) {
    return
  }
  commentScrollFrame = window.requestAnimationFrame(() => {
    commentScrollFrame = 0
    if (isCommentPagerMode.value || commentLoading.value || commentNoMore.value || !comments.value.length) {
      return
    }
    const nearBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 140
    if (nearBottom) {
      void loadMoreComments()
    }
  })
}

watch(
  articleUrl,
  value => {
    void updateQrImage(value)
  },
  { immediate: true }
)

onMounted(() => {
  syncLikedState()
  window.addEventListener('scroll', onCommentScroll, { passive: true })
  document.addEventListener('click', onDocumentAnchorClick)
  if (route.hash) {
    nextTick(() => {
      void settleRouteHashVisible(route.hash, 'auto')
    })
  }
})

watch(
  () => route.hash,
  hash => {
    if (!import.meta.client || !hash) {
      return
    }
    nextTick(() => {
      void settleRouteHashVisible(hash, 'smooth')
    })
  }
)

watch(
  () => content.value?.ID,
  () => syncLikedState()
)

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onCommentScroll)
  document.removeEventListener('click', onDocumentAnchorClick)
  if (commentScrollFrame) {
    window.cancelAnimationFrame(commentScrollFrame)
    commentScrollFrame = 0
  }
  if (copyStateTimer) {
    clearTimeout(copyStateTimer)
    copyStateTimer = null
  }
})

useSeoMeta({
  title: () => seoTitle.value,
  description: () => pageDescription.value,
  keywords: () => pageKeywords.value,
  ogTitle: () => seoTitle.value,
  ogDescription: () => pageDescription.value,
  articlePublishedTime: () => content.value?.publishedAt || ''
})

useHead({
  link: [{ rel: 'canonical', href: `${siteBaseUrl}${route.path}` }]
})
</script>

<template>
  <article class="card post-page">
    <header>
      <h1>{{ content?.title }}</h1>
      <div class="meta-row">
        <span class="meta-item"><i class="fa-regular fa-calendar" aria-hidden="true" />{{ content?.publishedAt ? new Date(content.publishedAt).toLocaleString('zh-CN') : '未发布' }}</span>
        <span class="meta-item"><i class="fa-regular fa-user" aria-hidden="true" />{{ content?.authorName || '佚名' }}</span>
        <span class="meta-item"><i class="fa-regular fa-eye" aria-hidden="true" />{{ content?.viewCount || 0 }} 阅读</span>
        <span class="meta-item"><i class="fa-regular fa-heart" aria-hidden="true" />{{ content?.likeCount || 0 }} 点赞</span>
        <span class="meta-item"><i class="fa-regular fa-comment" aria-hidden="true" />{{ content?.commentCount || 0 }} 评论</span>
      </div>
      <div class="tag-row" v-if="(content?.tags || []).length">
        <NuxtLink v-for="tag in content?.tags || []" :key="tag.ID" :to="`/tag/${encodeURIComponent(tag.slug || tag.name)}`" class="tag-chip">
          #{{ tag.name }}
        </NuxtLink>
      </div>
    </header>

    <div v-if="hasToc" ref="tocBoxRef" class="toc-box">
      <BlogPostToc
        :items="tocItems"
        :collapsed="tocCollapsed"
        collapsible
        @toggle="toggleToc"
      />
    </div>

    <BlogMarkdownContent
      :source="content?.markdown || content?.content"
      :toc-enabled="showToc"
      :post-cards="postCards"
      @toc-change="onTocChange"
    />
    <BlogAttachmentDownloads :attachments="content?.attachments" />
    <div class="post-tools">
      <BlogLikeButton
        variant="normal"
        :liked="liked"
        :effect-token="likeEffectToken"
        @click="likeCurrentContent"
      />
      <button type="button" class="content-action-btn" @click="qrOpen = true">
        <i class="fa-solid fa-qrcode" aria-hidden="true" />
        文章二维码
      </button>
      <button type="button" class="content-action-btn share-btn" @click="copyShareLink">
        <i class="fa-solid fa-link" aria-hidden="true" />
        {{ copyState === 'success' ? '已复制链接' : copyState === 'error' ? '复制失败' : '复制分享链接' }}
      </button>
    </div>
  </article>

  <Transition name="qr-pop">
    <div v-if="qrOpen" class="qr-mask" @click.self="qrOpen = false">
      <div class="card qr-modal">
        <button class="qr-close" type="button" aria-label="关闭" @click="qrOpen = false">
          <i class="fa-solid fa-xmark" aria-hidden="true" />
        </button>
        <h3>扫码访问文章</h3>
        <BlogImage v-if="qrImageUrl" :src="qrImageUrl" :alt="content?.title || '文章二维码'" loading="eager" />
        <p v-else>二维码生成中...</p>
        <p>{{ articleUrl }}</p>
      </div>
    </div>
  </Transition>

  <section id="comments" v-if="hasComments || allowComment" class="comments">
    <h2 id="comments-title"><i class="fa-regular fa-comment-dots" aria-hidden="true" />评论区</h2>

    <BlogCommentForm v-if="allowComment && !replyingTo" :content-id="content?.ID || 0" :parent-id="0" @submitted="onCommentSubmitted" />
    <div class="tree-wrap">
      <BlogCommentListSkeleton v-if="showCommentPagerSkeleton || showCommentInfiniteSkeleton" :rows="3" />
      <BlogCommentTree
        v-else
        :comments="comments"
        :gravatar-mirror="gravatarMirror"
        :replying-id="replyingTo?.ID"
        :replying-parent-id="replyingTo?.parentId"
        :allow-reply="allowComment"
        @reply="onReply"
      >
        <template v-if="allowComment" #reply-form="{ item }">
          <BlogCommentForm
            :content-id="content?.ID || 0"
            :parent-id="item.ID"
            :reply-author="replyingTo?.author || '匿名用户'"
            @cancel-reply="clearReply"
            @submitted="payload => onCommentSubmitted(payload, item)"
          />
        </template>
      </BlogCommentTree>
      <BlogPager
        v-if="isCommentPagerMode"
        :page="currentCommentPage"
        :page-size="commentPageSize"
        :total="commentTotal"
        query-key="cpage"
        scroll-target="#comments-title"
      />
      <template v-else>
        <BlogCommentListSkeleton v-if="commentLoading" :rows="2" />
        <BlogLoadingIndicator v-if="commentLoading" />
        <div v-else-if="hasMoreComments" class="comment-more-tip">下滑加载更多评论</div>
      </template>
    </div>
  </section>
</template>

<style scoped>
.post-page {
  padding: 20px;
}

h1 {
  font-size: 36px;
}

.meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 10px;
  margin-bottom: 12px;
  color: var(--text-soft);
  font-size: 13px;
}

.meta-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.tag-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 18px;
}

.tag-chip {
  padding: 4px 8px;
  border-radius: 999px;
  border: 0;
  color: var(--text-soft);
  background: var(--bg);
  font-size: 12px;
  text-decoration: none;
}

.post-tools {
  margin-top: 14px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.content-action-btn {
  border: 0;
  border-radius: 999px;
  padding: 9px 14px;
  color: var(--text);
  background: var(--bg);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 7px;
}

.qr-mask {
  position: fixed;
  inset: 0;
  z-index: 70;
  display: grid;
  place-items: center;
  padding: 14px;
  background: rgba(0, 0, 0, 0.35);
}

.qr-modal {
  position: relative;
  width: min(340px, 100%);
  padding: 16px;
  display: grid;
  gap: 10px;
  justify-items: center;
  box-shadow: var(--floating-shadow);
}

.qr-modal h3 {
  font-size: 18px;
}

.qr-modal img {
  width: 220px;
  height: 220px;
  border-radius: 10px;
  border: 1px solid var(--line);
  background: #fff;
}

.qr-modal p {
  max-width: 100%;
  color: var(--text-soft);
  font-size: 12px;
  text-align: center;
  overflow-wrap: anywhere;
}

.qr-pop-enter-active,
.qr-pop-leave-active {
  transition: opacity 0.2s ease;
}

.qr-pop-enter-from,
.qr-pop-leave-to {
  opacity: 0;
}

.qr-pop-enter-active .qr-modal,
.qr-pop-leave-active .qr-modal {
  transition:
    transform 0.2s ease,
    opacity 0.2s ease;
}

.qr-pop-enter-from .qr-modal,
.qr-pop-leave-to .qr-modal {
  opacity: 0;
  transform: translateY(8px) scale(0.98);
}

.qr-close {
  position: absolute;
  top: 8px;
  right: 8px;
  border: 0;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  color: var(--text-soft);
  background: transparent;
  cursor: pointer;
}

.toc-box {
  margin-bottom: 14px;
}

.comments {
  margin-top: 16px;
  display: grid;
  gap: 12px;
}

.comments h2 {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 26px;
  margin: 12px 0;
  scroll-margin-top: var(--anchor-offset);
}

.tree-wrap {
  margin-top: 4px;
  display: grid;
  gap: 12px;
}

.comment-more-tip {
  justify-self: center;
  font-size: 12px;
  color: var(--text-soft);
}

.comment-empty {
  margin-top: 14px;
  font-size: 15px;
}

@media (max-width: 760px) {
  h1 {
    font-size: 30px;
  }
}
</style>
